import { useEffect, useState } from 'react';
import {
  FiCheckCircle, FiAlertTriangle, FiShield, FiUser, FiCalendar,
  FiHash, FiAward, FiDownload, FiInfo, FiClock,
} from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import { verificacaoService, type ResultadoVerificacao, type AssinaturaVerificada } from '../../services/verificacao.service';
import { formatarDataBR, formatarDataHoraBR } from '../../lib/data';
import { useParams, useSearchParams } from 'react-router-dom';

const TIPO_ATO_LABEL: Record<string, string> = {
  portaria: 'Portaria',
  decreto: 'Decreto',
  lei: 'Lei',
  lei_complementar: 'Lei Complementar',
  resolucao: 'Resolução',
  instrucao_normativa: 'Instrução Normativa',
  edital: 'Edital',
  outros: 'Ato Oficial',
};

const MODULO_LABEL: Record<string, string> = {
  ato_oficial: 'Ato Oficial',
  diario_oficial: 'Diário Oficial Municipal',
  protocolo_legislativo: 'Protocolo Legislativo',
  oficio: 'Ofício',
  parecer: 'Parecer',
  memorando: 'Memorando',
  circular: 'Circular',
};

const TIPO_ASSINATURA_LABEL: Record<string, string> = {
  simples: 'Assinatura simples',
  avancada: 'Assinatura avançada (gov.br)',
  qualificada: 'Assinatura qualificada (ICP-Brasil)',
};

export function VerificarDocumentoPublicoPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const [searchParams] = useSearchParams();
  const hashOpcional = searchParams.get('h') ?? undefined;

  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [resultado, setResultado] = useState<ResultadoVerificacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroFatal, setErroFatal] = useState<string | null>(null);

  useEffect(() => {
    if (!codigo) return;
    setCarregando(true);
    setErroFatal(null);
    const estabId = getEstabelecimentoAtualId();
    Promise.all([
      verificacaoService.resolver(codigo, hashOpcional),
      supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', estabId).maybeSingle(),
    ])
      .then(([r, e]) => {
        setResultado(r);
        if (e.data) setEstab(e.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErroFatal(e instanceof Error ? e.message : 'Erro ao verificar'))
      .finally(() => setCarregando(false));
  }, [codigo, hashOpcional]);

  const nomeEstab = resultado?.estabelecimento?.nome ?? estab?.nome ?? 'Prefeitura Municipal';

  if (carregando) {
    return (
      <PortalPage
        titulo="Verificação de autenticidade"
        subtitulo={nomeEstab}
        icone={<FiShield size={26} />}
        largura="media"
      >
        <div className="flex flex-col items-center justify-center py-16">
          <Spinner tamanho="lg" />
          <p className="text-sm text-slate-600 mt-3">Verificando autenticidade...</p>
        </div>
      </PortalPage>
    );
  }

  if (erroFatal) {
    return (
      <PortalPage
        titulo="Verificação de autenticidade"
        subtitulo={nomeEstab}
        icone={<FiShield size={26} />}
        largura="media"
      >
        <Alert variante="erro">{erroFatal}</Alert>
      </PortalPage>
    );
  }

  // Documento nao encontrado ou erro de verificacao
  if (!resultado || !resultado.verificado) {
    return (
      <PainelResultado
        titulo="Documento NÃO autenticado"
        icone={<FiAlertTriangle size={32} />}
        tom="aviso"
        descricao={
          <>
            {resultado?.erro === 'documento_nao_encontrado' && 'Este código não corresponde a nenhum documento.'}
            {resultado?.erro === 'documento_nao_publicado' && 'O documento existe mas ainda não foi publicado.'}
            {resultado?.erro === 'documento_sem_assinatura' && 'O documento existe mas ainda não recebeu assinatura.'}
            {resultado?.erro === 'codigo_invalido' && 'Código de verificação inválido.'}
            {resultado?.erro === 'hash_invalido' && 'O hash do documento não confere — há indícios de adulteração.'}
            {!resultado?.erro && 'Não foi possível verificar este documento.'}
          </>
        }
      >
        <p className="text-center text-xs text-slate-500">
          Código consultado: <span className="font-mono">{codigo}</span>
        </p>
      </PainelResultado>
    );
  }

  const doc = resultado.documento!;
  const assinaturas = resultado.assinaturas ?? [];
  const resumo = resultado.resumo!;
  const tipoLabel = doc.tipo_ato ? TIPO_ATO_LABEL[doc.tipo_ato] ?? doc.tipo_ato : MODULO_LABEL[doc.modulo_codigo] ?? doc.modulo_codigo;
  const numeroExibicao = doc.numero_publicado ?? doc.numero;

  return (
    <PortalPage
      titulo="Verificação de autenticidade"
      subtitulo={nomeEstab}
      icone={<FiShield size={26} />}
      largura="media"
    >
      <div className="space-y-5">
        {/* Selo de status */}
        <SectionCard>
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <FiCheckCircle size={36} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-emerald-900">Documento autenticado</h2>
              <p className="text-sm text-slate-700 mt-0.5">
                {resultado.minimo
                  ? `Este documento existe nos registros da ${nomeEstab} e possui assinatura eletrônica registrada.`
                  : `Este documento existe nos registros da ${nomeEstab} e está publicado oficialmente.`}
              </p>
              {resultado.verificado_em && (
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <FiClock size={11} /> Verificação realizada em {formatarDataHoraBR(resultado.verificado_em)}
                </p>
              )}
            </div>
          </div>
        </SectionCard>

        {/* Integridade do conteúdo (quando o hash foi informado pelo QR) */}
        {resultado.integridade_ok === true && (
          <Alert variante="sucesso" className="flex items-start gap-2">
            <FiCheckCircle className="flex-shrink-0 mt-0.5" size={14} />
            <span><strong>Conteúdo íntegro.</strong> O documento confere com o original assinado — não foi alterado.</span>
          </Alert>
        )}
        {resultado.integridade_ok === false && (
          <Alert variante="erro" className="flex items-start gap-2">
            <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
            <span><strong>Atenção:</strong> o conteúdo apresentado <strong>não confere</strong> com o documento assinado. Há indícios de alteração.</span>
          </Alert>
        )}

        {/* Aviso simulação */}
        {resumo.todas_simuladas === true && resumo.total > 0 && (
          <Alert variante="aviso" className="flex items-start gap-2">
            <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
            <span>
              <strong>Aviso:</strong> as assinaturas digitais deste documento estão em modo SIMULAÇÃO.
              A integração com gov.br/ICP-Brasil ainda não foi ativada. O documento existe e está publicado,
              mas as assinaturas digitais qualificadas/avançadas ainda não têm valor jurídico real.
            </span>
          </Alert>
        )}

        {/* Dados do documento */}
        <SectionCard titulo="Dados do documento" icone={<FiAward size={18} />}>
          <dl className="space-y-2 text-sm">
            <Linha label="Tipo" valor={tipoLabel} />
            <Linha label="Número" valor={numeroExibicao} mono />
            {doc.numero_publicado && doc.numero_publicado !== doc.numero && (
              <Linha label="Número interno" valor={doc.numero} mono />
            )}
            {doc.ementa && <Linha label="Ementa" valor={doc.ementa} multiline />}
            {!doc.ementa && doc.titulo && <Linha label="Título" valor={doc.titulo} multiline />}
            {doc.autoridade_nome && (
              <Linha label="Autoridade" valor={`${doc.autoridade_nome}${doc.autoridade_cargo ? ` (${doc.autoridade_cargo})` : ''}`} />
            )}
            {doc.data_publicacao && <Linha label="Publicado em" valor={formatarDataBR(doc.data_publicacao)} />}
            {doc.hash_publico && <Linha label="Hash público" valor={doc.hash_publico} mono small />}
          </dl>

          {resultado.minimo && (
            <p className="mt-3 text-xs text-slate-500">
              Por se tratar de documento que pode conter informação restrita, esta página confirma apenas a
              autenticidade e a integridade — sem exibir o conteúdo. O documento completo é acessível aos envolvidos
              pelos canais oficiais.
            </p>
          )}

          {doc.pdf_url && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <a
                href={doc.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-institutional hover:underline"
              >
                <FiDownload size={14} /> Baixar PDF oficial
              </a>
            </div>
          )}
        </SectionCard>

        {/* Assinaturas */}
        <SectionCard titulo={`Assinaturas eletrônicas (${resumo.total})`} icone={<FiShield size={18} />}>
          {assinaturas.length === 0 ? (
            <div className="text-sm text-slate-500 italic flex items-center gap-2 py-2">
              <FiInfo size={14} />
              Este documento ainda não recebeu assinaturas eletrônicas registradas.
            </div>
          ) : (
            <div className="space-y-3">
              {assinaturas.map((a) => <ItemAssinatura key={a.id} a={a} />)}
            </div>
          )}
        </SectionCard>

        <p className="text-xs text-slate-500 text-center">
          Esta página é o registro oficial de verificação. Seu endereço permanente é{' '}
          <span className="font-mono break-all">{window.location.href}</span>
        </p>
      </div>
    </PortalPage>
  );
}

function Linha({ label, valor, mono, multiline, small }: { label: string; valor: string; mono?: boolean; multiline?: boolean; small?: boolean }) {
  return (
    <div className={`grid ${multiline ? 'grid-cols-1 md:grid-cols-[140px_1fr]' : 'grid-cols-[140px_1fr]'} gap-2`}>
      <dt className="text-slate-500 text-xs uppercase tracking-wider font-semibold">{label}</dt>
      <dd className={`text-slate-900 ${mono ? 'font-mono' : ''} ${small ? 'text-xs break-all' : ''} ${multiline ? '' : 'truncate'}`}>{valor}</dd>
    </div>
  );
}

function ItemAssinatura({ a }: { a: AssinaturaVerificada }) {
  const corBorda = a.tipo === 'qualificada' ? 'border-emerald-200 bg-emerald-50' : a.tipo === 'avancada' ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white';
  return (
    <div className={`border rounded p-3 ${corBorda}`}>
      <div className="flex items-baseline gap-2 flex-wrap mb-2">
        <FiCheckCircle className={a.tipo === 'qualificada' ? 'text-emerald-600' : a.tipo === 'avancada' ? 'text-blue-600' : 'text-slate-500'} size={14} />
        <span className="text-sm font-semibold text-slate-900">{a.signatario_nome}</span>
        <span className="text-xs text-slate-600">— {TIPO_ASSINATURA_LABEL[a.tipo]}</span>
        {a.simulada && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">SIMULADA</span>
        )}
      </div>

      <div className="ml-6 text-xs text-slate-700 space-y-1">
        <div className="flex items-center gap-1.5">
          <FiCalendar size={11} />
          <span>{formatarDataHoraBR(a.assinado_em)}</span>
        </div>
        {a.signatario_cpf && (
          <div className="flex items-center gap-1.5">
            <FiUser size={11} />
            <span>CPF: {a.signatario_cpf}</span>
          </div>
        )}
        {a.hash_conteudo && (
          <div className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500">
            <FiHash size={11} />
            <span title={a.hash_conteudo}>
              {a.hash_algoritmo}: {a.hash_conteudo.slice(0, 24)}...{a.hash_conteudo.slice(-8)}
            </span>
          </div>
        )}
        {a.tipo === 'qualificada' && a.certificado_emissor && (
          <div className="text-slate-500 mt-1 pt-1 border-t border-slate-200">
            <div>Emissor: {a.certificado_emissor}</div>
            {a.certificado_serial && <div className="font-mono text-[10px]">Serial: {a.certificado_serial}</div>}
            {a.carimbo_tempo && (
              <div>Carimbo: {formatarDataHoraBR(a.carimbo_tempo)}{a.carimbo_servidor && ` · ${a.carimbo_servidor}`}</div>
            )}
          </div>
        )}
        {a.fundamento_legal && (
          <div className="text-slate-400 mt-1 italic text-[10px]">{a.fundamento_legal}</div>
        )}
      </div>
    </div>
  );
}
